import {
  Form,
  Input,
  Stack,
  StatusLight,
  Table,
  TableColumn,
  TableNoRecords,
} from '@servicetitan/design-system';
import { useEmployeeTableState } from './useEmployeeTableState';
import statusValues, {
  StatusDotColor,
} from '@modules/UserStatusModule/statusValues';
import { UserSetAvailabilityStatus } from '@api/generated/graphql';
import { sanitizePhoneNumberTo10Digits } from '@lib/utils/formatPhone/sanitizePhoneNumberTo10Digits';
import { formatToNorthAmericanNumber } from '@lib/utils/formatPhone/formatToNorthAmericanNumber';
import ErrorComponent from '@components/ErrorComponent';
import styles from './OutboundModalEmployeeTable.module.css';
import { mainLayoutLocators } from '@locators/main-layout';
import { OutboundCallDetails } from '../../OutboundCallModal';
import { inboxLocators } from '@locators/inbox';

const PhoneNumberCell: React.FC<{ phone: string }> = ({ phone }) => {
  return (
    <td>{formatToNorthAmericanNumber(sanitizePhoneNumberTo10Digits(phone))}</td>
  );
};

const EmployeeSelectionRadioButtonCell: React.FC<{
  checked: boolean;
  id: string;
  phone: string;
  onClickHandler: (id: string, phone: string) => void;
}> = ({ checked, id, phone, onClickHandler }) => {
  return (
    <td>
      <Form.Radio
        id={`${mainLayoutLocators.navBar.outboundModal.employeeRowRadioBtn}-${id}`}
        checked={checked}
        label=''
        onClick={() => {
          onClickHandler(id, phone);
        }}
      />
    </td>
  );
};

/**
 * StatusCell Component
 *
 * This component is responsible for rendering a table cell (`<td>`) that displays
 * the status of a user with a corresponding status light indicator.
 *
 * @component
 * @param {Object} props - The properties object.
 * @param {UserSetAvailabilityStatus} props.status - The status of the user, which determines the color and text of the status light.
 *
 * @returns {JSX.Element} A table cell containing a status light with the appropriate color and status text.
 *
 * Example usage:
 * ```tsx
 * <StatusCell status={UserSetAvailabilityStatus.Online} />
 * ```
 */
const StatusCell: React.FC<{ status: UserSetAvailabilityStatus }> = ({
  status,
}) => {
  if (!status) {
    return <td />;
  }
  return (
    <td>
      <StatusLight color={StatusDotColor[status]}>
        {statusValues[status]}
      </StatusLight>
    </td>
  );
};

interface EmployeeTableProps {
  setCallDetails: (details: OutboundCallDetails) => void;
}

/**
 * OutboundModalEmployeeTable Component
 *
 * This component renders a table of employees that can be searched and selected.
 * Once the user selects an employee, the phone number of the selected employee is set using the `setNumber` function.
 * It includes a search input, a table with pagination, and various columns such as Agent, Status, and Phone Number.
 *
 * @component
 * @param {Object} props - The properties object.
 * @param {Function} props.setNumber - A function to set the phone number of the selected employee.
 *
 * @returns {JSX.Element} A stack containing a search input and a table of employees.
 *
 * Example usage:
 * ```tsx
 * <OutboundModalEmployeeTable setNumber={setNumberFunction} />
 * ```
 *
 */
const OutboundModalEmployeeTable: React.FC<EmployeeTableProps> = ({
  setCallDetails,
}) => {
  const state = useEmployeeTableState();

  return (
    <Stack direction='column'>
      <Input
        id={inboxLocators.callBar.searchInputUser}
        icon={{ name: 'search' }}
        iconPosition='left'
        className='m-b-1'
        size='small'
        width='100%'
        placeholder='Search for agent name or number'
        onChange={state.onSearchChange}
        value={state.search}
      />
      <Table
        selectable={{ enabled: true, mode: 'single' }}
        data={state.data}
        loading={state.loading}
        pageable={{ buttonCount: 3 }}
        pageSize={state.page.take}
        skip={state.page.skip}
        total={state.total}
        onPageChange={state.onPageChange}
        className={styles.EmployeeTable}
        rowHeight={40}
        id={inboxLocators.callBar.usersListTableToCall}
      >
        <TableNoRecords>
          <div className='ta-center m-x-auto p-y-6' style={{ width: 420 }}>
            <ErrorComponent
              image='block'
              title='No results found'
              body='Try another search.'
            />
          </div>
        </TableNoRecords>
        <TableColumn
          width={40}
          cell={(props) =>
            EmployeeSelectionRadioButtonCell({
              checked: props.dataItem.id === state.selectedId,
              id: props.dataItem.id,
              phone: props.dataItem.phone,
              onClickHandler: (id, phone) => {
                state.onSelectionChange(id);
                setCallDetails({
                  number: sanitizePhoneNumberTo10Digits(phone),
                  callDisplayName: props.dataItem.name,
                });
              },
            })
          }
        />
        <TableColumn title='Agent' field='name' />
        <TableColumn
          width={200} // to make sure we have enough space for statuses like "Away From Desk"
          title='Status'
          field='status'
          cell={(props) => StatusCell({ status: props.dataItem.status })}
        />
        <TableColumn
          title='Phone Number'
          field='phone'
          cell={(props) => PhoneNumberCell({ phone: props.dataItem.phone })}
        />
      </Table>
    </Stack>
  );
};

export default OutboundModalEmployeeTable;
